import { Button, Card, Form, Input, Modal, Space, message } from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import { ipBlacklistApi } from 'apis/sys';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import formatTime from 'utils/formatTime';

type IpBlacklistItem = Awaited<
  ReturnType<typeof ipBlacklistApi.list>
>['list'][number];

const useQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [ip, setIp] = useQueryState('ip', parseAsString.withDefault(''));

  return {
    pageNum,
    pageSize,
    ip,
    setPageNum,
    setPageSize,
    setIp,
  };
};

const IpBlacklistPage = () => {
  const { pageNum, pageSize, ip, setPageNum, setPageSize, setIp } =
    useQueryParams();
  const [addForm] = Form.useForm();
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<IpBlacklistItem>();

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const { list, total } = await ipBlacklistApi.list({
        pageNum,
        pageSize,
        ip: ip || undefined,
      });
      setDataSource(list);
      setTotal(total);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [ip, pageNum, pageSize, setDataSource, setLoading, setTotal]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const onRemove = useCallback(
    (record: IpBlacklistItem) => {
      Modal.confirm({
        title: '确认解封',
        content: `确定将 ${record.ip} 从黑名单移除？`,
        onOk: async () => {
          await ipBlacklistApi.remove({ id: record.id });
          message.success('已解封');
          getDataSource();
        },
      });
    },
    [getDataSource],
  );

  const onAdd = useCallback(async () => {
    const values = await addForm.validateFields();
    try {
      setSubmitting(true);
      await ipBlacklistApi.add({
        ip: values.ip,
        remark: values.remark,
      });
      message.success('已加入黑名单');
      setAddOpen(false);
      addForm.resetFields();
      // 已在第 1 页时直接刷新；否则只改 pageNum，由 useEffect 触发拉取，避免闭包旧 pageNum 双请求
      if (pageNum === 1) {
        await getDataSource();
      } else {
        setPageNum(1);
      }
    } finally {
      setSubmitting(false);
    }
  }, [addForm, getDataSource, pageNum, setPageNum]);

  const columns: ColumnsType<IpBlacklistItem> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 100,
      },
      {
        title: 'IP',
        dataIndex: 'ip',
        key: 'ip',
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        render: (value?: string | null) => value || '-',
      },
      {
        title: '操作人',
        dataIndex: 'createdBy',
        key: 'createdBy',
        render: (value?: string | null) => value || '-',
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime?: string) => formatTime(createTime),
      },
      {
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: 100,
        render: (_, record) => (
          <Button type="link" onClick={() => onRemove(record)}>
            解封
          </Button>
        ),
      },
    ],
    [onRemove],
  );

  return (
    <div className="space-y-4">
      <Card bodyStyle={{ padding: '16px' }}>
        <Form layout="inline">
          <Form.Item label="IP">
            <Input
              value={ip}
              onChange={(e) => {
                setIp(e.target.value);
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              allowClear
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                htmlType="button"
                onClick={() => {
                  setIp('');
                  setPageNum(1);
                  setPageSize(10);
                }}
              >
                <Trans i18nKey={LOCALS.reset} />
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  addForm.resetFields();
                  setAddOpen(true);
                }}
              >
                拉黑 IP
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <Table
          bordered
          pagination={{
            total,
            pageSize,
            current: pageNum,
            onChange: (page, nextPageSize) => {
              setPageNum(page);
              setPageSize(nextPageSize);
            },
          }}
          loading={loading}
          rowKey="id"
          dataSource={dataSource}
          columns={columns}
          scroll={{ x: 'max-content' }}
          tableLayout="auto"
        />
      </Card>

      <Modal
        open={addOpen}
        title="拉黑 IP"
        onCancel={() => setAddOpen(false)}
        onOk={onAdd}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            label="IP"
            name="ip"
            rules={[{ required: true, message: '请输入 IP' }]}
          >
            <Input placeholder="例如 1.2.3.4" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default IpBlacklistPage;
